/*
    implements UI properties collection
    used to add to dialogs editable fields linked with object where value will be read from and stored to
    describe the view template, do properties load and save between UI controls and properties struct
    handle UI controls values initialization and actions
    used in UI to display and edit group of properties
 */

class UIProperties {

    /*
        options:
            properties: predefined properties object
            propertiesFactory: async callback function(prop) which calls and fill properties on the each content invoking
            actionCb: calls when form's commit action is fired from outside with the action() method (for example window dialog's OK button)
            internalActionCallback: calls when form's commit action should be fired from a internal content component (for example Enter keypress inside one of input field)
            togglersCallbacks: toggle's callbacks, calls when a toggle switches
     */
    constructor(content, options) {
        this._content = document.createElement('div');
        this._content.innerHTML = content;
        this._actionCb = options.actionCb;
        this.registerInternalActionCallback(options.internalActionCallback);
        this.init(options.properties);
        this._propertiesFactory = options.propertiesFactory; //should be ASYNC
        this._actionOnEnterKey = null;
        // todo rename to controlCallbacks
        this._togglersCallbacks = options.togglersCallbacks;
        this._context = null;
        if (this._togglersCallbacks) {
            this._content.querySelectorAll('input[type="checkbox"],input[type="text"],input[type="range"]').forEach(function (el) {
                let control = el.getAttribute('data-bind');
                let controlType = el.getAttribute("type")
                if (control && this._togglersCallbacks[control]) {
                    if (controlType === "text") {
                        el.addEventListener('input', function (e) {
                            this._togglersCallbacks[control].call(this, e.target.value, function () {
                                // i don't know what to do on fail
                            })
                        }.bind(this))
                    } else {
                        el.addEventListener('change', function (e) {
                            if (controlType === "checkbox") {
                                this._togglersCallbacks[control].call(this, e.target.checked, function () {
                                    //on fail turn the toggle back
                                    e.target.checked = !e.target.checked;
                                })
                            } else {
                                this._togglersCallbacks[control].call(this, e.target.value, function () {
                                    // i don't know what to do on fail
                                })
                            }
                        }.bind(this))
                    }
                }
            }.bind(this));
        }
        // show/hide elements that depend on state of other checkboxes
        this._content.querySelectorAll('[data-depend-on]').forEach(function (el) {
            let depElement = this.getItem(el.getAttribute('data-depend-on'));
            if (!depElement)
                return
            let elementName = el.getAttribute('data-bind');
            depElement.addEventListener('change', function (e) {
                if (e.target.checked) {
                    this.show(elementName);
                } else {
                    this.hide(elementName);
                }
            }.bind(this))
        }.bind(this));
    }

    // action wrapper fired from outside
    // reads actual controls state, saves them to properties object
    // and calls actionCallback
    action() {
        for (let prop of Object.entries(this._properties)) {
            let propElement = this.getItem(prop[0]);
            if (!propElement)
                continue;
            //skip hidden properties
            if (propElement.style.display === 'none')
                continue;
            let value;
            if (propElement.type === 'checkbox') {
                value = propElement.checked;
            } else {
                value = propElement.value != null ? propElement.value : propElement.innerText;
            }
            this._properties[prop[0]] = value;
        }
        let rz = this._actionCb(this._properties, this._context);
        if (rz === undefined)
            return true
        return rz
    }

    show(item) {
        this._content.querySelectorAll("[data-bind='" + item + "']").forEach(function (el) {
            el.style.display = '';
        })
    }

    hide(item) {
        this._content.querySelectorAll("[data-bind='" + item + "']").forEach(function (el) {
            el.style.display = 'none';
        })
    }

    disable(item) {
        this._content.querySelectorAll("[data-bind='" + item + "']").forEach(function (el) {
            el.setAttribute("disabled", "")
        })
    }

    enable(item) {
        this._content.querySelectorAll("[data-bind='" + item + "']").forEach(function (el) {
            el.removeAttribute("disabled")
        })
    }
    getItem(item) {
        if (item === "")
            return null
        return this._content.querySelector('input[data-bind="' + item + '"], textarea[data-bind="' + item + '"]');
    }

    getItems(item) {
        if (item === "")
            return []
        let controls = this._content.querySelectorAll('input[data-bind="' + item + '"], textarea[data-bind="' + item + '"]');
        if (!controls || controls.length === 0) {
            controls = []
        }
        return controls
    }

    setItemValue(key, val) {
        let controls = this.getItems(key)

        controls.forEach( control => {
            let inputType = control.getAttribute("type")

            if (control.tagName === "TEXTAREA" ||
                inputType === "text" ||
                inputType === "range"
            )
                control.value = val;
            else if (control.tagName === "LABEL")
                control.innerText = val;
            else if (inputType === "checkbox") {
                control.checked = !!val
            } else if ('value' in control) {
                control.value = val
            }
        })

        // if this item isn't UI input but just controls
        // the visibility state of others elements
        // check elements that are depend on this key
        this._content.querySelectorAll(`[data-depend-on="${key}"]`).forEach(function (depElement) {
            let elementName = depElement.getAttribute('data-bind');
            //define initial state
            if (val) {
                if (depElement.hasAttribute("hideable")) {
                    this.show(elementName);
                } else {
                    this.enable(elementName);
                }
            } else {
                if (depElement.hasAttribute("hideable")) {
                    this.hide(elementName);
                } else {
                    this.disable(elementName);
                }
            }
        }.bind(this));

    }

    //initialize DOM input fields with properties values
    init(properties) {
        this._properties = properties;
        if (! properties)
            return;
        //fill properties
        for (let prop of Object.entries(this._properties)) {
            this.setItemValue(prop[0], prop[1])
        }
        //bind formatters
        //integer
        this._content.querySelectorAll('[data-format="integer"]').forEach( function (el) {
            el.addEventListener("keyup", function (ev) {
                ev.target.value = ev.target.value.replace(/[\D\s\._\-]+/g, "");
            })
        });

        //turn off/on elements depending the states of some other checkboxes
        this._content.querySelectorAll('[data-depend-on]').forEach(function (el) {
            let depElement = this.getItem(el.getAttribute('data-depend-on'));
            if (!depElement)
                return
            let elementName = el.getAttribute('data-bind');
            //define initial state
            if (depElement.checked) {
                this.show(elementName);
            } else {
                this.hide(elementName);
            }
        }.bind(this));
        return this;
    }

    registerInternalActionCallback(internalActionCallback) {
        this._internalActionCallback = internalActionCallback;
        this._initActionOnEnterKey();
    }

    //context: context object which will send to actionCallback
    registerContext(context) {
        this._context = context;
    }

    _initActionOnEnterKey() {
        if (! this._internalActionCallback)
            return;
        if (!this._actionOnEnterKey) {
            this._actionOnEnterKey = function (e) {
                if (e.keyCode === 13) {
                    if (this._internalActionCallback)
                        this._internalActionCallback();
                }
            }.bind(this);
            let el = this._content.querySelector('input[actionOnEnterKey]')
            if (el)
                el.addEventListener('keyup', this._actionOnEnterKey)
        }
    }

    //return DOM element object
    //fill DOM element with properties values
    content() {
        try {
            if (this._propertiesFactory) {
                if (! this._properties)
                    this._properties = {};
                this._propertiesFactory(this._properties).then( function () {
                    this.init(this._properties);
                }.bind(this));
            } else {
                this.init(this._properties);
            }
        } catch (e) {
            console.error(e);
        }

        return this._content;
    }
}

export {UIProperties};
